/**
 * A release or policy: the words, any documents to read (each opens in a new
 * tab), and the answer the step asks for: agree, yes or no (to one question or
 * several), choose one, or acknowledge. Some also ask for the family's full
 * name typed. Answers save as the guardian goes (see useStepAnswers). A
 * finished step reads back what was answered.
 */
import { ExternalLink } from 'lucide-react';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup } from '~/components/ui/radio-group';
import type { ConsentContext } from '../useChecklistStep';
import { Choice, Field, Section, StepFooter, StepHeader, type StepProps } from './stepParts';
import { useStepAnswers } from './useStepAnswers';

type ConsentAnswers = Record<string, unknown>;

function readAnswers(answers: unknown): ConsentAnswers {
  return answers && typeof answers === 'object' && !Array.isArray(answers) ? (answers as ConsentAnswers) : {};
}

function yesNo(value: unknown): string {
  return value === true ? 'Yes' : value === false ? 'No' : 'Not answered';
}

/** The answers given to a list of questions, by question key. */
function givenAnswers(answers: ConsentAnswers): Record<string, unknown> {
  const given = answers.answers;
  return given && typeof given === 'object' && !Array.isArray(given) ? (given as Record<string, unknown>) : {};
}

export default function ConsentStep({ step, readOnly, overviewPath, stepPath }: StepProps) {
  const consent = step.consent;
  if (!consent) return null;

  const subtitle = consent.childName ? `${consent.childName} · ${step.yearName}` : step.yearName;

  return (
    <div className="space-y-6">
      {/* The consent carries its own intro (with the child's name in it), shown with the words. */}
      <StepHeader step={{ ...step, intro: null }} subtitle={subtitle} />
      {readOnly ? (
        <>
          <Words consent={consent} />
          <Summary consent={consent} answers={readAnswers(step.answers)} />
        </>
      ) : (
        <ConsentForm step={step} consent={consent} overviewPath={overviewPath} stepPath={stepPath} />
      )}
    </div>
  );
}

/** What is being agreed to, and the documents behind it. */
function Words({ consent }: { consent: ConsentContext }) {
  return (
    <Section>
      {consent.intro ? <p className="whitespace-pre-line break-words">{consent.intro}</p> : null}
      <p className="whitespace-pre-line break-words">{consent.wording}</p>
      {consent.documents.length > 0 ? (
        <ul className="space-y-1">
          {consent.documents.map((document) => (
            <li key={document.url}>
              <a
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-2 break-words text-primary underline underline-offset-2"
              >
                {document.name}{document.label ? ` (${document.label})` : ''}
                <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

function Summary({ consent, answers }: { consent: ConsentContext; answers: ConsentAnswers }) {
  const lines: string[] = [];
  if (consent.answer === 'agree') lines.push(answers.agreed === true ? 'You agreed.' : 'Not agreed');
  if (consent.answer === 'acknowledge') lines.push(answers.acknowledged === true ? 'You acknowledged this.' : 'Not acknowledged');
  if (consent.answer === 'choice') {
    lines.push(consent.choices.find((choice) => choice.key === answers.choice)?.text ?? 'Not answered');
  }
  if (consent.answer === 'yes_no') {
    if (consent.questions.length > 0) {
      const given = givenAnswers(answers);
      consent.questions.forEach((question) => lines.push(`${question.text} ${yesNo(given[question.key])}`));
    } else {
      lines.push(yesNo(answers.answer));
    }
  }
  if (consent.typedName && typeof answers.typedName === 'string' && answers.typedName) {
    lines.push(`Signed by ${answers.typedName}`);
  }

  return (
    <Section>
      {lines.map((line, index) => <p key={index} className="break-words">{line}</p>)}
    </Section>
  );
}

function ConsentForm({ step, consent, overviewPath, stepPath }: {
  step: StepProps['step'];
  consent: ConsentContext;
  overviewPath: string;
  stepPath: (itemId: string) => string;
}) {
  const itemId = step.item.id;
  const { answers, change, submit, saveState, errors, submitting } = useStepAnswers<ConsentAnswers>({
    itemId,
    initial: readAnswers(step.answers),
    overviewPath,
    stepPath,
  });
  const set = (key: string, value: unknown) => change((current) => ({ ...current, [key]: value }));
  const given = givenAnswers(answers);

  return (
    <>
      <Words consent={consent} />

      {/* Held still while a submission is on its way. */}
      <fieldset disabled={submitting} className="min-w-0 space-y-6">
        <Section>
          {consent.answer === 'agree' ? (
            <CheckLine id={`${itemId}-agree`} label="I agree" checked={answers.agreed === true} onChange={(checked) => set('agreed', checked)} />
          ) : null}
          {consent.answer === 'acknowledge' ? (
            <CheckLine
              id={`${itemId}-acknowledge`}
              label="I have read this and acknowledge it"
              checked={answers.acknowledged === true}
              onChange={(checked) => set('acknowledged', checked)}
            />
          ) : null}
          {consent.answer === 'yes_no' && consent.questions.length === 0 ? (
            <YesNo id={`${itemId}-answer`} label="Your answer" value={answers.answer} onChange={(value) => set('answer', value)} hideLabel />
          ) : null}
          {consent.answer === 'yes_no' && consent.questions.length > 0 ? consent.questions.map((question) => (
            <YesNo
              key={question.key}
              id={`${itemId}-${question.key}`}
              label={question.text}
              value={given[question.key]}
              onChange={(value) => change((current) => ({ ...current, answers: { ...givenAnswers(current), [question.key]: value } }))}
            />
          )) : null}
          {consent.answer === 'choice' ? (
            <RadioGroup
              aria-label="Choose one"
              value={typeof answers.choice === 'string' ? answers.choice : ''}
              onValueChange={(choice) => set('choice', choice)}
              className="grid gap-2"
            >
              {consent.choices.map((choice) => (
                <Choice key={choice.key} id={`${itemId}-choice-${choice.key}`} value={choice.key} label={choice.text} />
              ))}
            </RadioGroup>
          ) : null}
        </Section>

        {consent.typedName ? (
          <Section>
            <Field id={`${itemId}-name`} label="Type your full name">
              <Input
                id={`${itemId}-name`}
                autoComplete="name"
                className="h-11 md:h-10"
                value={typeof answers.typedName === 'string' ? answers.typedName : ''}
                onChange={(event) => set('typedName', event.target.value)}
              />
            </Field>
          </Section>
        ) : null}
      </fieldset>

      <StepFooter errors={errors} saveState={saveState} submitting={submitting} label="Save and continue" onSubmit={submit} />
    </>
  );
}

function CheckLine({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div className="flex min-h-11 items-start gap-3">
      <Checkbox id={id} className="mt-0.5 h-5 w-5" checked={checked} onCheckedChange={(value) => onChange(value === true)} />
      <Label htmlFor={id} className="text-base font-normal leading-snug">{label}</Label>
    </div>
  );
}

function YesNo({ id, label, value, onChange, hideLabel = false }: {
  id: string;
  label: string;
  value: unknown;
  onChange: (value: boolean) => void;
  /** The wording above already says the question. */
  hideLabel?: boolean;
}) {
  return (
    <div className="space-y-2">
      <p id={`${id}-label`} className={hideLabel ? 'sr-only' : 'break-words'}>{label}</p>
      <RadioGroup
        aria-labelledby={`${id}-label`}
        value={value === true ? 'yes' : value === false ? 'no' : ''}
        onValueChange={(picked) => onChange(picked === 'yes')}
        className="grid gap-2 md:grid-cols-2"
      >
        <Choice id={`${id}-yes`} value="yes" label="Yes" />
        <Choice id={`${id}-no`} value="no" label="No" />
      </RadioGroup>
    </div>
  );
}
